import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import type { MembershipCategory } from '@/utils/membership/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { RequirementManager } from '../../requirements/RequirementManager'
import type { CategoryRequirement } from '../../requirements/types'
import { mapRequirementFromApi } from '../../requirements/utils'
import { categoryFormSchema, type CategoryFormValues } from '../schema'
import type { CategoryWithRequirements } from '../types'

type TabValue = 'basic' | 'requirements'

const TABS: { value: TabValue; label: string }[] = [
  { value: 'basic', label: 'Basic Info' },
  { value: 'requirements', label: 'Requirements' },
]

interface CategoryFormProps {
  defaultValues?: Partial<MembershipCategory> | CategoryWithRequirements
  onSubmit: (values: CategoryFormValues) => void
  isSubmitting?: boolean
  onCancel?: () => void
}

export function CategoryForm({ defaultValues, onSubmit, isSubmitting, onCancel }: CategoryFormProps) {
  const [currentTab, setCurrentTab] = useState<TabValue>('basic')
  const initialRequirements = useMemo((): CategoryRequirement[] => {
    const source = Array.isArray(defaultValues?.requirements) ? defaultValues?.requirements : []

    const normalised: CategoryRequirement[] = source
      .map((requirement) => {
        if (!requirement) return undefined
        if (
          typeof requirement === 'object' &&
          requirement !== null &&
          'label' in requirement &&
          'type' in requirement
        ) {
          return requirement as CategoryRequirement
        }
        return mapRequirementFromApi(
          requirement as MembershipCategory['requirements'] extends Array<infer R>
            ? R
            : never,
        )
      })
      .filter((requirement): requirement is CategoryRequirement => Boolean(requirement?.label))

    return normalised
  }, [defaultValues?.requirements])

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      categoryName: defaultValues?.categoryName || '',
      description: defaultValues?.description || '',
      annualFee: defaultValues?.annualFee || 0,
      active: defaultValues?.active ?? true,
      targetAudience: defaultValues?.targetAudience || '',
      requirements: initialRequirements,
    },
  })

  const getCurrentTabIndex = () => TABS.findIndex((tab) => tab.value === currentTab)
  const isLastTab = getCurrentTabIndex() === TABS.length - 1
  const isFirstTab = getCurrentTabIndex() === 0

  const validateCurrentTab = async (): Promise<boolean> => {
    let fieldsToValidate: (keyof CategoryFormValues)[] = []

    switch (currentTab) {
      case 'basic':
        fieldsToValidate = ['categoryName', 'annualFee', 'targetAudience', 'active']
        break
      case 'requirements':
        fieldsToValidate = ['requirements']
        break
    }

    const result = await form.trigger(fieldsToValidate)
    return result
  }

  const handleNext = async () => {
    const isValid = await validateCurrentTab()
    if (isValid && !isLastTab) {
      const nextIndex = getCurrentTabIndex() + 1
      setCurrentTab(TABS[nextIndex].value)
    }
  }

  const handlePrevious = () => {
    if (!isFirstTab) {
      const prevIndex = getCurrentTabIndex() - 1
      setCurrentTab(TABS[prevIndex].value)
    }
  }

  const handleFormSubmit = async () => {
    const isValid = await validateCurrentTab()
    if (isValid && isLastTab) {
      form.handleSubmit(onSubmit)()
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="flex flex-col flex-1 min-h-0">
        <Tabs value={currentTab} onValueChange={(value) => setCurrentTab(value as TabValue)} className="flex flex-col flex-1 min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            {TABS.map((tab, index) => (
              <TabsTrigger 
                key={tab.value} 
                value={tab.value}
                disabled={index > getCurrentTabIndex()}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="basic" className="flex-1 overflow-y-auto space-y-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="categoryName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Category Name</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Gold, Silver, Bronze" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="annualFee"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Annual Membership Fee (UGX)</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="0"
                        value={field.value ? field.value.toLocaleString('en-US') : ''}
                        onChange={(e) => {
                          // Remove commas and parse as number
                          const rawValue = e.target.value.replace(/,/g, '')
                          const numValue = parseFloat(rawValue) || 0
                          field.onChange(numValue)
                        }}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="targetAudience"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target Audience</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="e.g., Individual professionals, Corporate entities, Students"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Describe who this membership category is designed for
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active Status</FormLabel>
                    <FormDescription>
                      Make this category available for new member registrations
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="A brief overview of this membership category."
                      className="resize-y"
                      rows={10}
                      maxLength={250}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>

          <TabsContent value="requirements" className="flex-1 overflow-y-auto space-y-6 mt-4">
            <FormField
              control={form.control}
              name="requirements"
              render={({ field }) => (
                <FormItem>
                  <RequirementManager
                    requirements={field.value || []}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>
        </Tabs>

        <div className="flex justify-between items-center pt-4 border-t mt-6">
          <div>
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            {!isFirstTab && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={handlePrevious} 
                disabled={isSubmitting}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Previous
              </Button>
            )}
            {!isLastTab ? (
              <Button 
                type="button" 
                onClick={handleNext} 
                disabled={isSubmitting}
              >
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button type="button" onClick={handleFormSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Save Category
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </form>
    </Form>
  )
}
